<template>
  <layout-report-step
    :md="md"
    :isDone="isDone"
    :title="header.title"
    :description="header.description"
    :showFooter="false"
  >
    <v-card flat max-width="1200" class="pl-4 pr-4">
      <v-form ref="form" v-model="isValidForm" id="form" @submit.prevent="checkZip">
        <v-text-field
          v-model="zip"
          type="text"
          :label="$t('report.thgzip.label')"
          :rules="rules"
          maxlength="5"
          required
          outlined
          :append-icon="icon"
          data-test-form-zip
          @input="checkZip"
          :loading="isValidationLoading"
        />
      </v-form>
      <v-card-text v-if="!zip || zip.length < 5 || isValidationLoading || containsAlpha" class="mx-n4"></v-card-text>
      <div v-else-if="!isValidForm">
        <v-card-text class="mx-n4">{{ $t("report.thgzip.isNotAllowed") }}</v-card-text>
        <v-btn block color="success" @click="switchToWkthg">{{ $t("report.thgzip.altAction") }}</v-btn>
      </div>
      <v-card-text v-else class="mx-n4">{{ $t("report.thgzip.isAllowed", { cityName: cityName }) }}</v-card-text>
    </v-card>
  </layout-report-step>
</template>

<script lang="ts">
import LayoutReportStep from "@/layouts/LayoutReportStep.vue";
import { IReportHeaderData } from "@/lib/interfaces/Report/IReportHeaderData";
import { IReportStep } from "@/lib/interfaces/Report/IViewReportStep";
import { thgQueryParams } from "@/lib/queryParams/thgQueryParams";
import { germanZipRule } from "@/lib/rules/contactRule";
import { isNumberRule } from "@/lib/rules/isNumberRule";
import { requiredRule } from "@/lib/rules/requiredRule";
import { ReportModule } from "@/store/modules/report.store";
import { ThgCreateModule } from "@/store/modules/thg.create.store";
import { ZipModule } from "@/store/modules/zip.store";
import { Component, Vue } from "vue-property-decorator";
import ReportStepMixin from "@/mixins/ReportStepMixin.vue";
import { ReportScreenEnum } from "@/store/enum/partner/report-screen.enum";

@Component({
  components: {
    LayoutReportStep
  }
})
export default class ThgZip extends ReportStepMixin implements IReportStep {
  md = 4;
  header: IReportHeaderData = {
    title: "report.thgzip.title",
    description: "report.thgzip.description"
  };

  isValidationLoading = false;
  isValidZip = false;
  isValidForm = false;

  screen = ReportScreenEnum.thgzip;

  get cityName() {
    return ZipModule.checkZipResult.zip.city;
  }

  get zip() {
    return ThgCreateModule.address.zip;
  }

  set zip(zip: string) {
    ThgCreateModule.setAddress({ zip: zip });
  }

  get containsAlpha() {
    return !/^\d+$/.test(this.zip);
  }

  get rules() {
    return [requiredRule(), isNumberRule(), germanZipRule(), () => this.isValidZip || ""];
  }

  get icon(): string {
    if (this.isValidationLoading) {
      return "mdi-spin mdi-refresh";
    }
    if (this.isValidForm) {
      return "mdi-check-circle-outline";
    }
    return "mdi-close-circle-outline";
  }

  get form(): Vue & { validate: () => boolean } {
    return this.$refs.form as Vue & { validate: () => boolean };
  }

  get isDone() {
    return this.isValidForm;
  }

  get partnerId() {
    return ReportModule.partner.id || ReportModule.partner._id;
  }

  /**
   *
   */
  switchToWkthg() {
    const queryParams: string[] = [];
    thgQueryParams.forEach((q) => {
      const value = q.get();
      if (value) {
        if (q.key != "headless") {
          queryParams.push(`${q.key}=${q.get()}`);
        }
      }
    });
    const queryParamString = queryParams.join("&");

    window.open(`https://app.wirkaufendeinethg.de/#/?code=${this.partnerId}&${queryParamString}`, "_blank");
  }

  /**
   * Validate zip if it is already set e.g. from store
   */
  async mounted() {
    this.setCurrentStep();
    if (this.zip) {
      await this.checkZip();
    }
  }

  /**
   * Validate zip
   */
  async checkZip() {
    try {
      if (!this.zip || this.zip.length !== 5) {
        this.isValidZip = false;
        return;
      }
      this.isValidationLoading = true;
      const res = await ZipModule.checkZip({
        partnerId: this.partnerId,
        zip: this.zip
      });

      this.isValidZip = res.isServiceAvailable;
    } catch (e) {
      this.$log.error(e);
      this.$toast((e as any).message);
    } finally {
      this.isValidationLoading = false;
      this.form.validate();
    }
  }
}
</script>
